/**
 * Wheel Lab — closed short-put cycles from the own ledger.
 *
 * Question: did assignments happen because POP was too low (a slightly
 * further-OTM strike would have expired), or did the stock move through
 * even a conservative Wheel strike?
 *
 * Historical greeks are not published, so missing open_pop is filled at 70%.
 * Missing IV is left blank — it is not invented the way POP is.
 * Counterfactuals use the stock path (open price → close price vs strike)
 * plus this book's typical OTM% at a given POP — not reconstructed option
 * chains. The OTM-at-POP fit is still pivoted at 68% (low end of the band).
 */

export const ASSUMED_PUT_POP = 0.7;
/** Pivot for the book’s typical-OTM fit and slider extra-OTM vs “68%”. */
export const BOOK_BASE_POP = 0.68;

/** Extra OTM (as a fraction of open spot) that a slightly higher POP typically adds. */
export const AVOIDABLE_EXTRA_OTM = 0.035;
/** Extra OTM that ~80% POP typically adds vs 68% on 21–45 DTE equity. */
export const BORDERLINE_EXTRA_OTM = 0.07;

const FALLBACK_OTM_AT_68 = 0.06;
const FALLBACK_OTM_PER_POP_POINT = 0.0045;

export type PutOutcome = "expired" | "assigned" | "btc" | "open";
export type AssignClass = "avoidable" | "borderline" | "unavoidable";
export type VerdictKind = "raise-pop" | "keep-or-lower" | "mixed" | "no-assigns" | "thin-data";

export interface LedgerPutTxn {
  id?: number;
  linked_transaction_id?: number;
  linked_transaction_ids?: number[];
  instrument_type?: string;
  action?: string;
  side?: string;
  ticker?: string;
  trade_date?: string;
  quantity?: number;
  avg_price?: number;
  strike?: number;
  exp_date?: string;
  close_reason?: string;
  option_trans_code?: string;
  total_credit?: number;
  underlying_open_price?: number;
  underlying_close_price?: number;
  underlying_price?: number;
  open_pop?: number;
  open_pop_source?: string;
  open_iv?: number;
  open_delta?: number;
  open_gamma?: number;
  open_theta?: number;
  open_vega?: number;
}

export interface PutCycle {
  ticker: string;
  strike: number;
  contracts: number;
  openDate: string;
  closeDate: string | null;
  expDate: string | null;
  dte: number | null;
  underlyingOpen: number | null;
  underlyingClose: number | null;
  pop: number;
  popAssumed: boolean;
  premium: number;
  collateral: number;
  outcome: PutOutcome;
  otmPct: number | null;
  movePct: number | null;
  closeMoneyness: number | null;
  extraOtmNeeded: number | null;
  assignDepth: number | null;
  assignClass: AssignClass | null;
  closeCall: boolean;
  /** First later mark after close: share sale VWAP, leftover marked at live quote. */
  laterPx: number | null;
  laterDate: string | null;
  laterSource: LaterPriceSource | null;
}

export type LaterPriceSource = "sold" | "quote" | "mixed";

export interface PopBandRow {
  id: string;
  label: string;
  closed: number;
  assigned: number;
  expired: number;
  btc: number;
  assignRate: number | null;
  premium: number;
  collateral: number;
  yieldPct: number | null;
  assumed: number;
}

export interface ExtraOtmBucket {
  id: string;
  label: string;
  count: number;
}

export interface CounterfactualRow {
  targetPop: number;
  extraOtmVs68: number;
  avoided: number;
  stillAssigned: number;
  avoidedPct: number | null;
  stillPct: number | null;
  avoidedPremium: number;
  premiumDelta: number;
  assignMarkDelta: number;
  pnlDelta: number;
}

export interface WhatIfModel {
  targetPop: number;
  extraOtmVs68: number;
  pricedAssigned: number;
  avoided: number;
  stillAssigned: number;
  newAssigned: number;
  newAssignedStocks: number;
  newAssignedPct: number | null;
  avoidedPct: number | null;
  stillPct: number | null;
  actualPremium: number;
  modeledPremium: number;
  premiumDelta: number;
  actualAssignMark: number;
  modeledAssignMark: number;
  assignMarkDelta: number;
  actualPnl: number;
  modeledPnl: number;
  pnlDelta: number;
  pnlDeltaPct: number | null;
  avoidedPremium: number;
  avoidedAssignMark: number;
  /** Extra assigns whose later mark is back at or above the modeled strike. */
  newAssignedRebound: number;
  newAssignedWorse: number;
  newAssignedLaterPriced: number;
}

export interface WheelLabReport {
  cycles: PutCycle[];
  closed: PutCycle[];
  assigned: PutCycle[];
  expired: PutCycle[];
  btc: PutCycle[];
  open: PutCycle[];
  priced: PutCycle[];
  closeCalls: PutCycle[];
  assumedPopCount: number;
  missingPriceCount: number;
  assignClassCounts: Record<AssignClass, number>;
  extraOtmBuckets: ExtraOtmBucket[];
  popBands: PopBandRow[];
  otmModel: OtmPopModel;
  counterfactuals: CounterfactualRow[];
  verdict: {
    kind: VerdictKind;
    title: string;
    body: string;
  };
  totals: {
    closed: number;
    assigned: number;
    assignRate: number | null;
    premium: number;
    collateral: number;
    yieldPct: number | null;
  };
  /** Net short-option premium (puts + calls: STO − BTC) in this year scope. */
  contractIncome: number;
  contractIncomeByYear: Record<string, number>;
  contractIncomeByMonth: Record<string, number>;
}

export interface OtmPopModel {
  otmAt68: number;
  otmPerPopPoint: number;
  sample: number;
  empirical: boolean;
}

type PutKind = "sto" | "asgn" | "exp" | "btc";

export function popAsRatio(raw: number | null | undefined): number | null {
  const value = Number(raw);
  if (!Number.isFinite(value) || value <= 0) return null;
  return value > 1 ? value / 100 : value;
}

export function normalizeLedgerTxn(raw: Record<string, unknown>): LedgerPutTxn {
  const num = (value: unknown): number | undefined => {
    if (value == null) return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  };
  const pos = (value: unknown): number | undefined => {
    const parsed = num(value);
    return parsed != null && parsed > 0 ? parsed : undefined;
  };
  const linkedIdsRaw = raw.linked_transaction_ids;
  const linkedIds = Array.isArray(linkedIdsRaw)
    ? linkedIdsRaw.map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0)
    : undefined;
  let credit = num(raw.total_credit);
  if (credit == null && raw.extensions && typeof raw.extensions === "object") {
    credit = num((raw.extensions as Record<string, unknown>).total_credit);
  }
  return {
    id: num(raw.id),
    linked_transaction_id: num(raw.linked_transaction_id),
    linked_transaction_ids: linkedIds,
    instrument_type: typeof raw.instrument_type === "string" ? raw.instrument_type : undefined,
    action: typeof raw.action === "string" ? raw.action : undefined,
    side: typeof raw.side === "string" ? raw.side : undefined,
    ticker: typeof raw.ticker === "string" ? raw.ticker : undefined,
    trade_date: typeof raw.trade_date === "string" ? raw.trade_date : undefined,
    quantity: num(raw.quantity),
    avg_price: num(raw.avg_price),
    strike: pos(raw.strike),
    exp_date: typeof raw.exp_date === "string" ? raw.exp_date : undefined,
    close_reason: typeof raw.close_reason === "string" ? raw.close_reason : undefined,
    option_trans_code: typeof raw.option_trans_code === "string" ? raw.option_trans_code : undefined,
    total_credit: credit,
    underlying_open_price: pos(raw.underlying_open_price),
    underlying_close_price: pos(raw.underlying_close_price),
    underlying_price: pos(raw.underlying_price),
    open_pop: pos(raw.open_pop),
    open_pop_source: typeof raw.open_pop_source === "string" ? raw.open_pop_source : undefined,
    open_iv: (() => {
      const parsed = num(raw.open_iv);
      return parsed != null && parsed >= 0 ? parsed : undefined;
    })(),
    open_delta: num(raw.open_delta),
    open_gamma: num(raw.open_gamma),
    open_theta: num(raw.open_theta),
    open_vega: num(raw.open_vega),
  };
}

function txnCode(txn: LedgerPutTxn): string {
  return (txn.option_trans_code ?? "").toUpperCase();
}

function isPut(txn: LedgerPutTxn): boolean {
  return txn.instrument_type === "option" && (txn.side ?? "").toLowerCase() === "put";
}

function shortOptionSignedPremium(txn: LedgerPutTxn): number | null {
  if (txn.instrument_type !== "option") return null;
  const code = txnCode(txn);
  if (txn.close_reason === "assigned" || code === "OASGN") return null;
  if (txn.close_reason === "expired" || code === "OEXP") return null;
  if (txn.action === "sell") {
    if (code === "STC") return null;
    return premiumOf(txn);
  }
  if (txn.action === "buy") {
    if (code === "BTO") return null;
    return -premiumOf(txn);
  }
  return null;
}

function sumShortOptionIncome(txns: LedgerPutTxn[]): {
  total: number;
  byYear: Record<string, number>;
} {
  const byYear: Record<string, number> = {};
  let total = 0;
  for (const txn of txns) {
    const signed = shortOptionSignedPremium(txn);
    if (signed == null) continue;
    total += signed;
    const year = dateKey(txn.trade_date).slice(0, 4);
    if (year.length === 4) byYear[year] = (byYear[year] ?? 0) + signed;
  }
  return { total, byYear };
}

function putKind(txn: LedgerPutTxn): PutKind | null {
  if (!isPut(txn)) return null;
  const code = txnCode(txn);
  if (txn.close_reason === "assigned" || code === "OASGN") return "asgn";
  if (txn.close_reason === "expired" || code === "OEXP") return "exp";
  if (txn.action === "sell") {
    if (code === "STC") return null;
    return "sto";
  }
  if (txn.action === "buy") {
    if (code === "BTO") return null;
    return "btc";
  }
  return null;
}

function linkedIds(txn: LedgerPutTxn): number[] {
  const ids = new Set<number>();
  const primary = Number(txn.linked_transaction_id);
  if (Number.isFinite(primary) && primary > 0) ids.add(primary);
  for (const id of txn.linked_transaction_ids ?? []) {
    if (Number.isFinite(id) && id > 0) ids.add(id);
  }
  return [...ids];
}

function contractKey(txn: LedgerPutTxn): string | null {
  if (!txn.ticker || txn.strike == null || !txn.exp_date) return null;
  return `${txn.ticker.toUpperCase()}|${txn.strike}|${txn.exp_date}`;
}

function contractsOf(txn: LedgerPutTxn): number {
  return Math.abs(Number(txn.quantity) || 0);
}

function premiumOf(txn: LedgerPutTxn): number {
  const qty = contractsOf(txn);
  const avg = Math.abs(Number(txn.avg_price) || 0);
  if (qty > 0 && avg > 0) {
    const perShare = qty * avg * 100;
    const perContract = qty * avg;
    const net = Math.abs(Number(txn.total_credit) || 0);
    if (net > 0) {
      const dShare = Math.abs(perShare - net);
      const dContract = Math.abs(perContract - net);
      if (dContract + 5 < dShare) return perContract;
    }
    return perShare;
  }
  const credit = Number(txn.total_credit);
  return Number.isFinite(credit) ? Math.abs(credit) : 0;
}

function dateKey(value: string | undefined | null): string {
  return (value ?? "").slice(0, 10);
}

function optionDte(openDate: string | null, expDate: string | null): number | null {
  if (!openDate || !expDate) return null;
  const start = Date.parse(`${openDate}T12:00:00Z`);
  const end = Date.parse(`${expDate}T12:00:00Z`);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  return Math.round((end - start) / 86_400_000);
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function classifyAssign(extraOtmNeeded: number | null, assignDepth: number | null): AssignClass | null {
  const extra = extraOtmNeeded ?? assignDepth;
  if (extra == null || !Number.isFinite(extra)) return null;
  if (extra <= AVOIDABLE_EXTRA_OTM) return "avoidable";
  if (extra <= BORDERLINE_EXTRA_OTM) return "borderline";
  return "unavoidable";
}

function resolvePop(open: LedgerPutTxn, close: LedgerPutTxn | null): { pop: number; assumed: boolean } {
  const captured = popAsRatio(open.open_pop) ?? popAsRatio(close?.open_pop ?? undefined);
  if (captured != null) return { pop: captured, assumed: false };
  return { pop: ASSUMED_PUT_POP, assumed: true };
}

function resolveSpot(
  open: LedgerPutTxn,
  close: LedgerPutTxn | null,
): { s0: number | null; st: number | null } {
  const s0 =
    open.underlying_open_price ??
    close?.underlying_open_price ??
    open.underlying_price ??
    null;
  const st =
    close?.underlying_close_price ??
    close?.underlying_price ??
    open.underlying_close_price ??
    null;
  return { s0, st };
}

function finishCycle(
  open: LedgerPutTxn,
  close: LedgerPutTxn | null,
  outcome: PutOutcome,
  qty: number,
): PutCycle | null {
  const ticker = (open.ticker ?? close?.ticker ?? "").toUpperCase();
  const strike = Number(open.strike ?? close?.strike);
  if (!ticker || !(strike > 0) || qty <= 0) return null;

  const { pop, assumed } = resolvePop(open, close);
  const { s0, st } = resolveSpot(open, close);
  const openDate = dateKey(open.trade_date) || dateKey(close?.trade_date);
  const closeDate = close ? dateKey(close.trade_date) : null;
  const expDate = dateKey(open.exp_date) || dateKey(close?.exp_date) || null;
  const otmPct = s0 != null && s0 > 0 ? (s0 - strike) / s0 : null;
  const movePct = s0 != null && st != null && s0 > 0 ? (st - s0) / s0 : null;
  const closeMoneyness = st != null && strike > 0 ? (st - strike) / strike : null;
  const extraOtmNeeded =
    outcome === "assigned" && s0 != null && st != null && s0 > 0
      ? Math.max(0, (strike - st) / s0)
      : outcome === "assigned" && st != null
        ? Math.max(0, (strike - st) / strike)
        : null;
  const assignDepth =
    outcome === "assigned" && st != null && strike > 0 ? Math.max(0, (strike - st) / strike) : null;
  const closeCall =
    outcome !== "assigned" &&
    outcome !== "open" &&
    closeMoneyness != null &&
    closeMoneyness >= 0 &&
    closeMoneyness <= 0.02;

  const stoPrem = premiumOf(open) * (qty / Math.max(contractsOf(open), qty));
  const closePrem = close && outcome === "btc" ? premiumOf(close) * (qty / Math.max(contractsOf(close), qty)) : 0;

  return {
    ticker,
    strike,
    contracts: qty,
    openDate,
    closeDate,
    expDate,
    dte: optionDte(openDate, expDate),
    underlyingOpen: s0,
    underlyingClose: st,
    pop,
    popAssumed: assumed,
    premium: stoPrem - closePrem,
    collateral: strike * qty * 100,
    outcome,
    otmPct,
    movePct,
    closeMoneyness,
    extraOtmNeeded,
    assignDepth,
    assignClass: outcome === "assigned" ? classifyAssign(extraOtmNeeded, assignDepth) : null,
    closeCall,
    laterPx: null,
    laterDate: null,
    laterSource: null,
  };
}

interface OpenLot {
  txn: LedgerPutTxn;
  remaining: number;
}

function consumeOpen(lot: OpenLot, qty: number): number {
  const take = Math.min(lot.remaining, qty);
  lot.remaining -= take;
  return take;
}

export function pairPutCycles(txns: LedgerPutTxn[]): PutCycle[] {
  const puts = txns.filter((txn) => putKind(txn) != null);
  const byId = new Map<number, LedgerPutTxn>();
  for (const txn of puts) {
    if (txn.id != null) byId.set(txn.id, txn);
  }

  const opens: OpenLot[] = puts
    .filter((txn) => putKind(txn) === "sto")
    .sort(
      (a, b) =>
        dateKey(a.trade_date).localeCompare(dateKey(b.trade_date)) || (a.id ?? 0) - (b.id ?? 0),
    )
    .map((txn) => ({ txn, remaining: contractsOf(txn) }));

  const closes = puts
    .filter((txn) => {
      const kind = putKind(txn);
      return kind === "asgn" || kind === "exp" || kind === "btc";
    })
    .sort(
      (a, b) =>
        dateKey(a.trade_date).localeCompare(dateKey(b.trade_date)) || (a.id ?? 0) - (b.id ?? 0),
    );

  const cycles: PutCycle[] = [];
  const usedClose = new Set<number>();

  const takeMatchingOpen = (
    close: LedgerPutTxn,
    qty: number,
    preferIds: Set<number>,
  ): { open: LedgerPutTxn; qty: number }[] => {
    const taken: { open: LedgerPutTxn; qty: number }[] = [];
    let left = qty;
    const tryLot = (lot: OpenLot): void => {
      if (left <= 0 || lot.remaining <= 0) return;
      const sameTicker =
        (lot.txn.ticker ?? "").toUpperCase() === (close.ticker ?? "").toUpperCase();
      if (!sameTicker) return;
      const take = consumeOpen(lot, left);
      if (take > 0) {
        taken.push({ open: lot.txn, qty: take });
        left -= take;
      }
    };

    if (preferIds.size) {
      for (const lot of opens) {
        if (lot.txn.id != null && preferIds.has(lot.txn.id)) tryLot(lot);
      }
    }
    const key = contractKey(close);
    if (left > 0 && key) {
      for (const lot of opens) {
        if (contractKey(lot.txn) === key) tryLot(lot);
      }
    }
    return taken;
  };

  for (const close of closes) {
    const qty = contractsOf(close);
    if (qty <= 0) continue;
    const prefer = new Set<number>();
    for (const id of linkedIds(close)) prefer.add(id);
    for (const lot of opens) {
      if (lot.txn.id != null && linkedIds(lot.txn).includes(close.id ?? -1)) {
        prefer.add(lot.txn.id);
      }
    }
    const matches = takeMatchingOpen(close, qty, prefer);
    const kind = putKind(close);
    const outcome: PutOutcome = kind === "asgn" ? "assigned" : kind === "exp" ? "expired" : "btc";
    if (matches.length) {
      usedClose.add(close.id ?? -1);
      for (const match of matches) {
        const cycle = finishCycle(match.open, close, outcome, match.qty);
        if (cycle) cycles.push(cycle);
      }
    } else {
      const orphanOpen: LedgerPutTxn = {
        ...close,
        action: "sell",
        close_reason: undefined,
        option_trans_code: "STO",
      };
      const cycle = finishCycle(orphanOpen, close, outcome, qty);
      if (cycle) cycles.push(cycle);
    }
  }

  const today = todayIso();
  for (const lot of opens) {
    if (lot.remaining <= 0) continue;
    const exp = dateKey(lot.txn.exp_date);
    const outcome: PutOutcome = exp && exp < today ? "expired" : "open";
    const cycle = finishCycle(lot.txn, null, outcome, lot.remaining);
    if (cycle) cycles.push(cycle);
  }

  cycles.sort(
    (a, b) =>
      (b.closeDate ?? b.openDate).localeCompare(a.closeDate ?? a.openDate) ||
      a.ticker.localeCompare(b.ticker),
  );
  return cycles;
}

interface StockSellLot {
  ticker: string;
  date: string;
  price: number;
  remaining: number;
}

function extractStockSells(rawTxns: Record<string, unknown>[]): StockSellLot[] {
  const sells: StockSellLot[] = [];
  for (const raw of rawTxns) {
    const type = String(raw.instrument_type ?? "").toLowerCase();
    if (type !== "stock" && type !== "equity") continue;
    const action = String(raw.action ?? "").toLowerCase();
    if (action !== "sell") continue;
    const status = String(raw.status ?? raw.order_status ?? raw.close_reason ?? "").toLowerCase();
    if (status && /cancel|reject|unfill|void/.test(status)) continue;
    const ticker = String(raw.ticker ?? "").toUpperCase();
    const date = dateKey(typeof raw.trade_date === "string" ? raw.trade_date : undefined);
    const price = Number(raw.avg_price ?? raw.price);
    const qty = Math.abs(Number(raw.quantity) || 0);
    if (!ticker || date.length < 10 || !(price > 0) || !(qty > 0)) continue;
    sells.push({ ticker, date, price, remaining: qty });
  }
  sells.sort((a, b) => a.date.localeCompare(b.date) || a.ticker.localeCompare(b.ticker));
  return sells;
}

function applyLaterMark(
  cycle: PutCycle,
  laterPx: number,
  laterDate: string,
  laterSource: LaterPriceSource,
): void {
  cycle.laterPx = laterPx;
  cycle.laterDate = laterDate;
  cycle.laterSource = laterSource;
}

/** Attach post-close marks: assigned lots use subsequent stock sales, leftover + unassigned use live quotes. */
export function attachLaterPrices(
  cycles: PutCycle[],
  rawTxns: Record<string, unknown>[],
  quotes: Map<string, number>,
): void {
  const sells = extractStockSells(rawTxns);
  const assigned = cycles
    .filter((cycle) => cycle.outcome === "assigned" && cycle.closeDate)
    .sort(
      (a, b) =>
        (a.closeDate ?? "").localeCompare(b.closeDate ?? "") || a.ticker.localeCompare(b.ticker),
    );

  for (const cycle of assigned) {
    const closeDate = cycle.closeDate!;
    const need = cycle.contracts * 100;
    let taken = 0;
    let notional = 0;
    let lastSellDate = closeDate;
    for (const sell of sells) {
      if (sell.ticker !== cycle.ticker || sell.remaining <= 0) continue;
      if (sell.date < closeDate) continue;
      const take = Math.min(sell.remaining, need - taken);
      if (take <= 0) continue;
      sell.remaining -= take;
      taken += take;
      notional += take * sell.price;
      lastSellDate = sell.date;
      if (taken >= need) break;
    }
    const leftover = need - taken;
    const quote = quotes.get(cycle.ticker);
    if (taken > 0 && leftover > 0 && quote != null && quote > 0) {
      applyLaterMark(cycle, (notional + leftover * quote) / need, lastSellDate, "mixed");
    } else if (taken > 0) {
      applyLaterMark(cycle, notional / taken, lastSellDate, "sold");
    } else if (quote != null && quote > 0) {
      applyLaterMark(cycle, quote, todayIso(), "quote");
    }
  }

  for (const cycle of cycles) {
    if (cycle.laterPx != null) continue;
    if (cycle.outcome === "open") continue;
    const quote = quotes.get(cycle.ticker);
    if (quote != null && quote > 0) applyLaterMark(cycle, quote, todayIso(), "quote");
  }
}

export function laterMovePct(cycle: PutCycle): number | null {
  if (cycle.underlyingOpen == null || !(cycle.underlyingOpen > 0) || cycle.laterPx == null) return null;
  return (cycle.laterPx - cycle.underlyingOpen) / cycle.underlyingOpen;
}

export function reboundVsCost(laterPx: number | null | undefined, cost: number | null | undefined): boolean | null {
  if (laterPx == null || cost == null || !(cost > 0)) return null;
  return laterPx + 0.01 >= cost;
}

export function fellWorseAfterClose(cycle: PutCycle): boolean | null {
  if (cycle.laterPx == null || cycle.underlyingClose == null) return null;
  return cycle.laterPx + 0.01 < cycle.underlyingClose;
}

function median(values: number[]): number | null {
  if (!values.length) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2;
}

export function fitOtmPopModel(cycles: PutCycle[]): OtmPopModel {
  const priced = cycles.filter(
    (cycle) => cycle.otmPct != null && Number.isFinite(cycle.otmPct) && cycle.outcome !== "open",
  );
  const around68 = priced
    .filter((cycle) => cycle.pop >= 0.64 && cycle.pop <= 0.72)
    .map((cycle) => cycle.otmPct!)
    .filter((otm) => otm > -0.05 && otm < 0.35);
  const higher = priced.filter((cycle) => cycle.pop >= 0.74 && cycle.otmPct != null && cycle.otmPct! > -0.05);
  const otmAt68 = median(around68) ?? FALLBACK_OTM_AT_68;
  let otmPerPopPoint = FALLBACK_OTM_PER_POP_POINT;
  let empirical = around68.length >= 6;
  if (higher.length >= 4 && around68.length >= 4) {
    const highOtm = median(higher.map((cycle) => cycle.otmPct!));
    const highPop = median(higher.map((cycle) => cycle.pop));
    if (highOtm != null && highPop != null && highPop > 0.7) {
      const slope = (highOtm - otmAt68) / ((highPop - BOOK_BASE_POP) * 100);
      if (Number.isFinite(slope) && slope >= 0.002 && slope <= 0.01) {
        otmPerPopPoint = slope;
        empirical = true;
      }
    }
  }
  return {
    otmAt68,
    otmPerPopPoint,
    sample: priced.length,
    empirical,
  };
}

export function estimatedOtmForPop(model: OtmPopModel, pop: number): number {
  return model.otmAt68 + (pop - BOOK_BASE_POP) * 100 * model.otmPerPopPoint;
}

/** Invert the book’s OTM-at-POP fit. */
export function popForOtm(model: OtmPopModel, otm: number): number {
  const per = model.otmPerPopPoint > 0 ? model.otmPerPopPoint : FALLBACK_OTM_PER_POP_POINT;
  return BOOK_BASE_POP + (otm - model.otmAt68) / (100 * per);
}

/** OTM buffer at open that would have expired this path (strike ≤ close). */
export function otmToExpire(cycle: PutCycle): number | null {
  if (cycle.underlyingOpen == null || !(cycle.underlyingOpen > 0) || cycle.underlyingClose == null) {
    return null;
  }
  return (cycle.underlyingOpen - cycle.underlyingClose) / cycle.underlyingOpen;
}

export interface AvoidPopEstimate {
  neededOtm: number;
  neededPop: number;
  capped: "low" | "high" | null;
}

/** Lowest POP whose estimated strike still finishes at or above the close. */
export function popToAvoidAssignment(cycle: PutCycle, model: OtmPopModel): AvoidPopEstimate | null {
  const neededOtm = otmToExpire(cycle);
  if (neededOtm == null) return null;
  const raw = popForOtm(model, neededOtm + 0.0001);
  if (raw < 0.5) return { neededOtm, neededPop: 0.5, capped: "low" };
  if (raw > 0.95) return { neededOtm, neededPop: 0.95, capped: "high" };
  return { neededOtm, neededPop: raw, capped: null };
}

/** Extra OTM vs this book’s typical 68% strike. Positive = safer / further OTM. */
export function extraOtmVs68(model: OtmPopModel, targetPop: number): number {
  return estimatedOtmForPop(model, targetPop) - model.otmAt68;
}

/**
 * Actual sold strike shifted by extra OTM vs 68%.
 * At 68% this is the real strike, so the slider is a delta vs the current book
 * rather than a replacement with the typical OTM at that POP.
 */
export function estimatedStrikeForPop(
  cycle: PutCycle,
  model: OtmPopModel,
  targetPop: number,
): number | null {
  if (cycle.underlyingOpen == null || !(cycle.underlyingOpen > 0)) return null;
  if (!(cycle.strike > 0)) return null;
  const strike = cycle.strike - extraOtmVs68(model, targetPop) * cycle.underlyingOpen;
  return strike > 0 ? strike : null;
}

export function wouldSurviveAtPop(cycle: PutCycle, model: OtmPopModel, targetPop: number): boolean | null {
  if (cycle.outcome !== "assigned") return null;
  const assigned = wouldAssignAtPop(cycle, model, targetPop);
  if (assigned == null) return null;
  return !assigned;
}

/** True if close finished below the estimated strike for this POP. */
export function wouldAssignAtPop(cycle: PutCycle, model: OtmPopModel, targetPop: number): boolean | null {
  if (cycle.underlyingClose == null) return null;
  const strike = estimatedStrikeForPop(cycle, model, targetPop);
  if (strike == null) return null;
  return cycle.underlyingClose + 0.01 < strike;
}

/** Scale collected premium by 1 − POP (short-put delta ≈ 1 − POP). Lower POP → more premium. */
export function scalePremiumToPop(premium: number, soldPop: number, targetPop: number): number {
  const sold = Math.min(0.95, Math.max(0.5, soldPop));
  const target = Math.min(0.95, Math.max(0.5, targetPop));
  const scale = (1 - target) / (1 - sold);
  return premium * Math.min(2.5, Math.max(0.25, scale));
}

function assignMark(closePx: number, strike: number, contracts: number): number {
  return (closePx - strike) * contracts * 100;
}

export function modelWhatIf(
  closed: PutCycle[],
  assigned: PutCycle[],
  model: OtmPopModel,
  targetPop: number,
): WhatIfModel {
  const pricedAssigned = assigned.filter(
    (cycle) => cycle.underlyingOpen != null && cycle.underlyingClose != null,
  );
  let avoided = 0;
  let stillAssigned = 0;
  let newAssigned = 0;
  let newAssignedRebound = 0;
  let newAssignedWorse = 0;
  let newAssignedLaterPriced = 0;
  const extraTickers = new Set<string>();
  let pricedUnassigned = 0;
  let avoidedPremium = 0;
  let avoidedAssignMark = 0;
  let actualPremium = 0;
  let modeledPremium = 0;
  let actualAssignMark = 0;
  let modeledAssignMark = 0;

  for (const cycle of closed) {
    actualPremium += cycle.premium;
    modeledPremium += scalePremiumToPop(cycle.premium, cycle.pop, targetPop);
  }

  const extraOtm = extraOtmVs68(model, targetPop);
  const saferThanBook = extraOtm > 1e-6;
  const riskierThanBook = extraOtm < -1e-6;

  for (const cycle of assigned) {
    const survived = wouldSurviveAtPop(cycle, model, targetPop);
    // Avoided only when the slider is safer than the actual book. A lower POP
    // moves the strike closer to spot, so historical assigns stay assigned.
    if (saferThanBook && survived === true) {
      avoided += 1;
      avoidedPremium += cycle.premium;
    }
    if (cycle.underlyingClose == null) continue;
    const mark = assignMark(cycle.underlyingClose, cycle.strike, cycle.contracts);
    actualAssignMark += mark;
    if (saferThanBook && survived === true) avoidedAssignMark += mark;
    // Still assigned: keep the actual strike mark. Do not reprice at a
    // higher/lower estimated strike — that invented ITM and made lower POP
    // look worse. Avoided assigns drop the mark.
    if (!(saferThanBook && survived === true)) modeledAssignMark += mark;
  }

  for (const cycle of closed) {
    if (cycle.outcome === "assigned") continue;
    if (cycle.underlyingClose == null) continue;
    if (estimatedStrikeForPop(cycle, model, targetPop) == null) continue;
    pricedUnassigned += 1;
    // Extra assigns only when the slider is more aggressive than the actual
    // book. A higher POP cannot create assignments the sold strike already beat.
    if (!riskierThanBook) continue;
    if (wouldAssignAtPop(cycle, model, targetPop) !== true) continue;
    const nextStrike = estimatedStrikeForPop(cycle, model, targetPop) ?? cycle.strike;
    modeledAssignMark += assignMark(cycle.underlyingClose, nextStrike, cycle.contracts);
    newAssigned += 1;
    extraTickers.add(cycle.ticker.toUpperCase());
    if (cycle.laterPx != null) {
      newAssignedLaterPriced += 1;
      if (reboundVsCost(cycle.laterPx, nextStrike) === true) newAssignedRebound += 1;
      if (fellWorseAfterClose(cycle) === true) newAssignedWorse += 1;
    }
  }

  // Still assigned is the original assigned book minus avoided. Extra assigns
  // are new names and must not change this count.
  stillAssigned = assigned.length - avoided;

  const premiumDelta = modeledPremium - actualPremium;
  const assignMarkDelta = modeledAssignMark - actualAssignMark;
  // Wheel P/L is premium (STO credit, BTC debit). Put assignment is stock at
  // strike — not an (close − strike) option P/L. Lower POP therefore raises
  // modeled P/L; assignment mark is a separate path/risk column.
  const actualPnl = actualPremium;
  const modeledPnl = modeledPremium;
  const pnlDelta = premiumDelta;
  const pctBase = Math.abs(actualPremium);
  return {
    targetPop,
    extraOtmVs68: extraOtm,
    pricedAssigned: pricedAssigned.length,
    avoided,
    stillAssigned,
    newAssigned,
    newAssignedStocks: extraTickers.size,
    newAssignedPct: pricedUnassigned ? newAssigned / pricedUnassigned : null,
    avoidedPct: pricedAssigned.length ? avoided / pricedAssigned.length : null,
    stillPct: pricedAssigned.length ? stillAssigned / pricedAssigned.length : null,
    actualPremium,
    modeledPremium,
    premiumDelta,
    actualAssignMark,
    modeledAssignMark,
    assignMarkDelta,
    actualPnl,
    modeledPnl,
    pnlDelta,
    pnlDeltaPct: pctBase > 0 ? pnlDelta / pctBase : null,
    avoidedPremium,
    avoidedAssignMark,
    newAssignedRebound,
    newAssignedWorse,
    newAssignedLaterPriced,
  };
}

function extraOtmBuckets(assigned: PutCycle[]): ExtraOtmBucket[] {
  const defs = [
    { id: "0-2", label: "0–2%", max: 0.02 },
    { id: "2-4", label: "2–4%", max: 0.04 },
    { id: "4-6", label: "4–6%", max: 0.06 },
    { id: "6-8", label: "6–8%", max: 0.08 },
    { id: "8-12", label: "8–12%", max: 0.12 },
    { id: "12+", label: "12%+", max: Number.POSITIVE_INFINITY },
  ];
  const counts = defs.map((def) => ({ id: def.id, label: def.label, count: 0 }));
  for (const cycle of assigned) {
    const extra = cycle.extraOtmNeeded ?? cycle.assignDepth;
    if (extra == null) continue;
    const idx = defs.findIndex((def) => extra <= def.max);
    const bucket = counts[idx < 0 ? counts.length - 1 : idx];
    if (bucket) bucket.count += 1;
  }
  return counts;
}

function popBandId(pop: number): string {
  if (pop < 0.68) return "lt68";
  if (pop < 0.75) return "68-75";
  if (pop < 0.8) return "75-80";
  return "80+";
}

function emptyBand(id: string, label: string): PopBandRow {
  return {
    id,
    label,
    closed: 0,
    assigned: 0,
    expired: 0,
    btc: 0,
    assignRate: null,
    premium: 0,
    collateral: 0,
    yieldPct: null,
    assumed: 0,
  };
}

function buildPopBands(closed: PutCycle[]): PopBandRow[] {
  const bands = [
    emptyBand("lt68", "< 68%"),
    emptyBand("68-75", "68–75%"),
    emptyBand("75-80", "75–80%"),
    emptyBand("80+", "80%+"),
  ];
  const byId = new Map(bands.map((band) => [band.id, band]));
  for (const cycle of closed) {
    const band = byId.get(popBandId(cycle.pop));
    if (!band) continue;
    band.closed += 1;
    if (cycle.outcome === "assigned") band.assigned += 1;
    if (cycle.outcome === "expired") band.expired += 1;
    if (cycle.outcome === "btc") band.btc += 1;
    band.premium += cycle.premium;
    band.collateral += cycle.collateral;
    if (cycle.popAssumed) band.assumed += 1;
  }
  for (const band of bands) {
    band.assignRate = band.closed ? band.assigned / band.closed : null;
    band.yieldPct = band.collateral > 0 ? band.premium / band.collateral : null;
  }
  return bands;
}

function buildCounterfactuals(
  closed: PutCycle[],
  assigned: PutCycle[],
  model: OtmPopModel,
): CounterfactualRow[] {
  return [0.5, 0.55, 0.6, 0.65, 0.68, 0.72, 0.75, 0.8, 0.85].map((targetPop) => {
    const whatIf = modelWhatIf(closed, assigned, model, targetPop);
    return {
      targetPop,
      extraOtmVs68: whatIf.extraOtmVs68,
      avoided: whatIf.avoided,
      stillAssigned: whatIf.stillAssigned,
      avoidedPct: whatIf.avoidedPct,
      stillPct: whatIf.stillPct,
      avoidedPremium: whatIf.avoidedPremium,
      premiumDelta: whatIf.premiumDelta,
      assignMarkDelta: whatIf.assignMarkDelta,
      pnlDelta: whatIf.pnlDelta,
    };
  });
}

function buildVerdict(
  closed: PutCycle[],
  assigned: PutCycle[],
  counts: Record<AssignClass, number>,
): WheelLabReport["verdict"] {
  if (closed.length < 5) {
    return {
      kind: "thin-data",
      title: "Not enough closed puts yet",
      body: "Need a larger book of resolved short puts (with open and close underlying prices) before a POP change is more than a guess. Keep writing in the 68–75% band and re-check here after more expirations.",
    };
  }
  if (!assigned.length) {
    return {
      kind: "no-assigns",
      title: "No put assignments in this history",
      body: "The 68–75% band is keeping you out of stock. You can stay here, or test a slightly lower POP on names you are willing to own if you want more premium — assignment risk has not shown up yet.",
    };
  }
  const classified = counts.avoidable + counts.borderline + counts.unavoidable;
  const avoidShare = classified ? counts.avoidable / classified : 0;
  const marketShare = classified ? counts.unavoidable / classified : 0;

  if (marketShare >= 0.55 && avoidShare < 0.35) {
    return {
      kind: "keep-or-lower",
      title: "Market moved through you — raising POP would not have saved most assignments",
      body: "Most assigned puts finished well below the strike. A 75–80% POP strike (a few extra percent OTM) would still have been in the money. Stay in the 68–75% band and keep harvesting premium; assignment here is a tail move, not a tight-strike problem.",
    };
  }
  if (avoidShare >= 0.55) {
    return {
      kind: "raise-pop",
      title: "Most assignments were shallow — a higher POP would likely have expired them",
      body: "The stock finished close to your strike. Choosing a modestly lower strike (75–80% POP) would have kept most of these from assigning. Raise POP if staying unassigned matters more than the extra premium at 68–75%.",
    };
  }
  return {
    kind: "mixed",
    title: "Mixed book — some tight misses, some hard selloffs",
    body: "Use the extra-OTM chart and the what-if table. If the 0–4% bucket is large, step toward 75% POP on names you do not want to own. If the 8%+ bucket dominates, keep 68–75% and treat assignment as the cost of the premium.",
  };
}

export function filterCyclesByYear(cycles: PutCycle[], year: string | "all"): PutCycle[] {
  if (year === "all") return cycles;
  return cycles.filter((cycle) => (cycle.closeDate || cycle.openDate).startsWith(year));
}

export function analyzePutCycles(rawTxns: Record<string, unknown>[]): WheelLabReport {
  const txns = rawTxns.map(normalizeLedgerTxn);
  const { total: contractIncome, byYear: contractIncomeByYear } = sumShortOptionIncome(txns);
  const cycles = pairPutCycles(txns);
  const closed = cycles.filter((cycle) => cycle.outcome !== "open");
  const assigned = closed.filter((cycle) => cycle.outcome === "assigned");
  const expired = closed.filter((cycle) => cycle.outcome === "expired");
  const btc = closed.filter((cycle) => cycle.outcome === "btc");
  const open = cycles.filter((cycle) => cycle.outcome === "open");
  const priced = closed.filter((cycle) => cycle.underlyingOpen != null && cycle.underlyingClose != null);
  const closeCalls = closed.filter((cycle) => cycle.closeCall);
  const assumedPopCount = closed.filter((cycle) => cycle.popAssumed).length;
  const missingPriceCount = closed.filter(
    (cycle) => cycle.underlyingOpen == null || cycle.underlyingClose == null,
  ).length;
  const assignClassCounts: Record<AssignClass, number> = {
    avoidable: assigned.filter((cycle) => cycle.assignClass === "avoidable").length,
    borderline: assigned.filter((cycle) => cycle.assignClass === "borderline").length,
    unavoidable: assigned.filter((cycle) => cycle.assignClass === "unavoidable").length,
  };
  const otmModel = fitOtmPopModel(closed);
  const premium = closed.reduce((sum, cycle) => sum + cycle.premium, 0);
  const collateral = closed.reduce((sum, cycle) => sum + cycle.collateral, 0);
  return {
    cycles,
    closed,
    assigned,
    expired,
    btc,
    open,
    priced,
    closeCalls,
    assumedPopCount,
    missingPriceCount,
    assignClassCounts,
    extraOtmBuckets: extraOtmBuckets(assigned),
    popBands: buildPopBands(closed),
    otmModel,
    counterfactuals: buildCounterfactuals(closed, assigned, otmModel),
    verdict: buildVerdict(closed, assigned, assignClassCounts),
    totals: {
      closed: closed.length,
      assigned: assigned.length,
      assignRate: closed.length ? assigned.length / closed.length : null,
      premium,
      collateral,
      yieldPct: collateral > 0 ? premium / collateral : null,
    },
    contractIncome,
    contractIncomeByYear,
    contractIncomeByMonth: {},
  };
}

function cyclePeriodDate(cycle: PutCycle): string {
  return cycle.closeDate || cycle.openDate;
}

export function filterCyclesByMonth(cycles: PutCycle[], month: string): PutCycle[] {
  return cycles.filter((cycle) => cyclePeriodDate(cycle).startsWith(month));
}

export function availableMonths(cycles: PutCycle[], year: string): string[] {
  const months = new Set<string>();
  for (const cycle of cycles) {
    if (cycle.outcome === "open") continue;
    const date = cyclePeriodDate(cycle);
    if (date.startsWith(`${year}-`) && date.length >= 7) months.add(date.slice(0, 7));
  }
  return [...months].sort();
}

export function reportForYear(report: WheelLabReport, year: string | "all"): WheelLabReport {
  return reportForPeriod(report, year, null);
}

export function reportForPeriod(
  report: WheelLabReport,
  year: string | "all",
  month: string | null,
): WheelLabReport {
  if (year === "all" && !month) return report;
  const yearCycles = filterCyclesByYear(report.cycles, year);
  const cycles = month ? filterCyclesByMonth(yearCycles, month) : yearCycles;
  const income = month
    ? (report.contractIncomeByMonth[month] ?? 0)
    : year === "all"
      ? report.contractIncome
      : (report.contractIncomeByYear[year] ?? 0);
  const next = analyzePutCyclesFromCycles(cycles, income, report.contractIncomeByYear, report.contractIncomeByMonth);
  if (month && (year === "all" ? report.closed.length : yearCycles.length) > cycles.length) {
    const broader = year === "all" ? report : analyzePutCyclesFromCycles(yearCycles, income, report.contractIncomeByYear, report.contractIncomeByMonth);
    next.otmModel = broader.otmModel;
    next.counterfactuals = buildCounterfactuals(next.closed, next.assigned, next.otmModel);
  }
  return next;
}

function analyzePutCyclesFromCycles(
  cycles: PutCycle[],
  contractIncome = 0,
  contractIncomeByYear: Record<string, number> = {},
  contractIncomeByMonth: Record<string, number> = {},
): WheelLabReport {
  const closed = cycles.filter((cycle) => cycle.outcome !== "open");
  const assigned = closed.filter((cycle) => cycle.outcome === "assigned");
  const assignClassCounts: Record<AssignClass, number> = {
    avoidable: assigned.filter((cycle) => cycle.assignClass === "avoidable").length,
    borderline: assigned.filter((cycle) => cycle.assignClass === "borderline").length,
    unavoidable: assigned.filter((cycle) => cycle.assignClass === "unavoidable").length,
  };
  const otmModel = fitOtmPopModel(closed);
  const premium = closed.reduce((sum, cycle) => sum + cycle.premium, 0);
  const collateral = closed.reduce((sum, cycle) => sum + cycle.collateral, 0);
  return {
    cycles,
    closed,
    assigned,
    expired: closed.filter((cycle) => cycle.outcome === "expired"),
    btc: closed.filter((cycle) => cycle.outcome === "btc"),
    open: cycles.filter((cycle) => cycle.outcome === "open"),
    priced: closed.filter((cycle) => cycle.underlyingOpen != null && cycle.underlyingClose != null),
    closeCalls: closed.filter((cycle) => cycle.closeCall),
    assumedPopCount: closed.filter((cycle) => cycle.popAssumed).length,
    missingPriceCount: closed.filter(
      (cycle) => cycle.underlyingOpen == null || cycle.underlyingClose == null,
    ).length,
    assignClassCounts,
    extraOtmBuckets: extraOtmBuckets(assigned),
    popBands: buildPopBands(closed),
    otmModel,
    counterfactuals: buildCounterfactuals(closed, assigned, otmModel),
    verdict: buildVerdict(closed, assigned, assignClassCounts),
    totals: {
      closed: closed.length,
      assigned: assigned.length,
      assignRate: closed.length ? assigned.length / closed.length : null,
      premium,
      collateral,
      yieldPct: collateral > 0 ? premium / collateral : null,
    },
    contractIncome,
    contractIncomeByYear,
    contractIncomeByMonth,
  };
}

export function availableYears(cycles: PutCycle[]): string[] {
  const years = new Set<string>();
  for (const cycle of cycles) {
    const date = cycle.closeDate || cycle.openDate;
    if (date.length >= 4) years.add(date.slice(0, 4));
  }
  return [...years].sort();
}
